/**
 * Utility functions for handling embeddable URLs and iframe functionality
 */

// Constants for better maintainability
const EMBED_CONSTANTS = {
  MAX_URL_LENGTH: 2048,
  MAX_TEXT_LENGTH: 10000,
  MIN_IFRAME_DIMENSION: 100,
  MAX_IFRAME_DIMENSION: 2000,
  DEFAULT_IFRAME_WIDTH: 800,
  DEFAULT_IFRAME_HEIGHT: 600,
  DEFAULT_IFRAME_TITLE: "Embedded Content",
  RESET_TIMEOUT: 2000,
  // Environment-aware domain whitelist for security
  ALLOWED_DOMAINS: (() => {
    const isDevelopment =
      process.env.NODE_ENV === "development" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const productionDomains = ["teamform.co", "orchestrated.io"];
    const developmentDomains = [...productionDomains, "localhost", "127.0.0.1"];

    return isDevelopment ? developmentDomains : productionDomains;
  })(),
};

/**
 * Centralized error handling for embed functionality
 * @param {string} context - The context where the error occurred
 * @param {Error} error - The error object
 * @param {string} severity - Error severity ('security', 'runtime', 'user')
 */
const handleEmbedError = (context, error, severity = "runtime") => {
  const errorMessage = `Embed ${context}: ${error.message}`;

  // Log for debugging - use reportError when available
  if (typeof window !== "undefined" && window.reportError) {
    window.reportError(errorMessage, error);
  } else {
    // Fallback for environments without reportError
    // eslint-disable-next-line no-console
    console.error(errorMessage, error);
  }

  // Report security errors
  if (
    severity === "security" &&
    typeof window !== "undefined" &&
    window.reportError
  ) {
    window.reportError(`Embed security issue in ${context}`, error);
  }

  // Could add user notification here for better UX
  // Could add analytics tracking here
};

/**
 * Validates and sanitizes a URL for embed functionality
 * @param {string} url - The URL to validate
 * @returns {string} The sanitized URL
 * @throws {Error} If URL is invalid or contains security issues
 */
export const validateAndSanitizeUrl = (url) => {
  try {
    const urlObj = new URL(url);

    // Sanitize pathname to prevent path traversal
    const sanitizedPath = urlObj.pathname.replace(/\.\./g, "");

    // Remove any potentially dangerous characters from pathname
    const cleanPath = sanitizedPath.replace(/[<>"'%]/g, "");

    // Reconstruct URL with sanitized components
    const cleanUrl = new URL(urlObj.origin + cleanPath + urlObj.search);

    // Validate URL length
    if (cleanUrl.href.length > EMBED_CONSTANTS.MAX_URL_LENGTH) {
      throw new Error("URL is too long for embed functionality");
    }

    return cleanUrl.toString();
  } catch (error) {
    handleEmbedError("URL validation", error, "security");
    throw new Error(`Invalid URL for embed: ${error.message}`);
  }
};

/**
 * Validates if a URL is safe for embedding
 * @param {string} url - The URL to validate
 * @returns {boolean} True if URL is safe for embedding
 */
export const isSafeEmbedUrl = (url) => {
  try {
    const urlObj = new URL(url);

    // Check for HTTPS (required for secure contexts)
    if (urlObj.protocol !== "https:") {
      return false;
    }

    // Check for allowed domains (basic check)
    const allowedDomains = EMBED_CONSTANTS.ALLOWED_DOMAINS;

    const hostname = urlObj.hostname.toLowerCase();
    const isAllowed = allowedDomains.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    );

    if (!isAllowed) {
      return false;
    }

    // Check for dangerous characters in pathname
    if (/[<>"'%]/.test(urlObj.pathname)) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Generates an embeddable URL for the current page
 * @param {string} baseUrl - The base URL to make embeddable (defaults to current URL)
 * @returns {string} The embeddable URL
 */
export const generateEmbedUrl = (baseUrl = window.location.href) => {
  try {
    // Validate and sanitize the input URL
    const sanitizedUrl = validateAndSanitizeUrl(baseUrl);
    const url = new URL(sanitizedUrl);

    // Add embed parameter to indicate this is an embeddable page
    url.searchParams.set("embed", "true");

    const finalUrl = url.toString();

    // Additional security check
    if (!isSafeEmbedUrl(finalUrl)) {
      throw new Error("Generated URL is not safe for embedding");
    }

    return finalUrl;
  } catch (error) {
    handleEmbedError("URL generation", error, "security");
    throw error;
  }
};

/**
 * Generates a simple embeddable URL (alias for generateEmbedUrl for backwards compatibility)
 * @param {string} baseUrl - The base URL to make embeddable
 * @returns {string} The embeddable URL
 */
export const generateSimpleEmbedUrl = (baseUrl = window.location.href) => {
  try {
    return generateEmbedUrl(baseUrl);
  } catch (error) {
    handleEmbedError("simple URL generation", error, "runtime");
    throw error;
  }
};

/**
 * Removes embed parameters from a URL
 * @param {string} embedUrl - The URL with embed parameters
 * @returns {string} The clean URL without embed parameters
 */
export const removeEmbedParams = (embedUrl) => {
  try {
    // Handle invalid input
    if (!embedUrl || typeof embedUrl !== "string") {
      return "";
    }

    // If it's already a full URL, use it directly
    if (embedUrl.startsWith("http")) {
      const url = new URL(embedUrl);
      url.searchParams.delete("embed");
      return url.toString();
    }

    // For relative URLs, try to construct a full URL
    if (
      typeof window !== "undefined" &&
      window.location &&
      window.location.origin
    ) {
      const url = new URL(embedUrl, window.location.origin);
      url.searchParams.delete("embed");
      return url.toString();
    }

    // Fallback for test environment or when window.location is not available
    const cleanedUrl = embedUrl.replace(/[?&]embed=[^&]*/, "");
    // Fix double question marks or ampersands
    return cleanedUrl.replace(/^[?&]+/, "?").replace(/^&/, "?");
  } catch (error) {
    handleEmbedError("parameter removal", error, "runtime");
    // Fallback: simple string replacement
    const cleanedUrl = embedUrl.replace(/[?&]embed=[^&]*/, "");
    return cleanedUrl.replace(/^[?&]+/, "?").replace(/^&/, "?");
  }
};

/**
 * Copies text to clipboard with modern API and fallback
 * @param {string} text - The text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    // Validate input
    if (!text || typeof text !== "string") {
      throw new Error("Invalid text provided for clipboard copy");
    }

    // Validate text length to prevent abuse
    if (text.length > EMBED_CONSTANTS.MAX_TEXT_LENGTH) {
      throw new Error("Text too long for clipboard copy");
    }

    // Use modern Clipboard API when available
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers or non-secure contexts
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(text);
      document.body.removeChild(textArea);
      return true;
    } catch (clipboardError) {
      // Fallback to deprecated execCommand (only for very old browsers)
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      return successful;
    }
  } catch (error) {
    handleEmbedError("clipboard copy", error, "user");
    return false;
  }
};

/**
 * Generates an iframe embed code for the current page
 * @param {Object} options - Configuration options
 * @param {string} options.url - The URL to embed (defaults to current URL)
 * @param {number} options.width - iframe width (default: 800)
 * @param {number} options.height - iframe height (default: 600)
 * @param {string} options.title - iframe title for accessibility
 * @returns {string} The iframe HTML code
 */
export const generateIframeCode = (options = {}) => {
  try {
    const {
      url = generateSimpleEmbedUrl(),
      title = EMBED_CONSTANTS.DEFAULT_IFRAME_TITLE,
    } = options;

    // Validate and sanitize inputs
    const sanitizedUrl = validateAndSanitizeUrl(url);
    const sanitizedTitle = String(
      title || EMBED_CONSTANTS.DEFAULT_IFRAME_TITLE
    ).replace(/[<>"'%]/g, "");

    // Use responsive iframe template
    return `<div style="position: relative; width: 100%; height: 0; padding-bottom: 75%;">
  <iframe 
    src="${sanitizedUrl}" 
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 1px solid #e0e0e0; border-radius: 6px;"
    frameborder="0"
    title="${sanitizedTitle}">
  </iframe>
</div>`;
  } catch (error) {
    handleEmbedError("iframe code generation", error, "runtime");
    throw error;
  }
};

/**
 * Checks if the current page is running in an iframe
 * @returns {boolean} True if running in an iframe
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};
/**
 * Determines if a link should open in a new window when in embed mode
 * @param {string} href - The href attribute of the link
 * @param {boolean} isEmbedMode - Whether the page is in embed mode
 * @returns {boolean} True if link should open in new window
 */
export const shouldOpenInNewWindow = (href, isEmbedMode) => {
  if (!isEmbedMode) {
    return false;
  }

  try {
    // Handle null/undefined href
    if (!href || typeof href !== "string") {
      return isEmbedMode;
    }

    // If it's an external link (different domain), it should open in new window
    if (href.startsWith("http") && !href.includes(window.location.hostname)) {
      return true;
    }

    // If it's an internal link (relative or same domain), it should open in new window
    if (href.startsWith("/") || href.includes(window.location.hostname)) {
      return true;
    }

    // For other cases (like anchors #section), keep default behavior
    return !href.startsWith("#");
  } catch (error) {
    handleEmbedError("link target determination", error, "runtime");
    // When in doubt in embed mode, open in new window
    return isEmbedMode;
  }
};

/**
 * Converts an embedded URL back to a regular URL for new window navigation
 * @param {string} url - The potentially embedded URL
 * @returns {string} Clean URL without embed parameters
 */
export const getCleanUrlForNewWindow = (url) => {
  try {
    if (!url || typeof url !== "string") {
      return window.location.href;
    }

    // If it's a relative URL, make it absolute
    let fullUrl = url;
    if (url.startsWith("/")) {
      // Handle test environment where window.location.origin might be undefined
      const origin = window.location.origin || "https://teamform.co";
      fullUrl = origin + url;
    }

    return removeEmbedParams(fullUrl);
  } catch (error) {
    handleEmbedError("URL cleaning for new window", error, "runtime");
    return removeEmbedParams(window.location.href);
  }
};

/**
 * Gets the appropriate target attribute for links in embed mode
 * @param {boolean} isEmbedMode - Whether the page is in embed mode
 * @param {string} href - The href attribute of the link
 * @param {string} defaultTarget - The default target if provided
 * @returns {string} The target attribute value
 */
export const getEmbedAwareTarget = (
  isEmbedMode,
  href,
  defaultTarget = "_self"
) => {
  if (!isEmbedMode) {
    return defaultTarget;
  }

  return shouldOpenInNewWindow(href, isEmbedMode) ? "_blank" : defaultTarget;
};
